import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { SEOHead } from '@/components/SEOHead';
import { Layout } from '@/components/layout/Layout';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Search, Building2, MapPin, Globe, Phone, Mail, Users, Shield, X,
  LayoutGrid, List, Home, ChevronRight as ChevronRightIcon
} from 'lucide-react';
import { useState } from 'react';

const CITIES = ['Addis Ababa', 'Adama', 'Hawassa', 'Bahir Dar', 'Mekelle', 'Dire Dawa', 'Jimma', 'Gondar'];

export default function Agencies() {
  const [search, setSearch] = useState('');
  const [cityFilter, setCityFilter] = useState<string | undefined>();
  const [sortBy, setSortBy] = useState('name_asc');
  const [layout, setLayout] = useState<'grid' | 'list'>('grid');

  const { data: agencies, isLoading } = useQuery({
    queryKey: ['agencies-page'],
    queryFn: async () => {
      const { data, error } = await supabase.from('agencies').select('*').order('name');
      if (error) throw error;
      const agencyIds = (data || []).map(a => a.id);
      const { data: agentProfiles } = await supabase.from('agent_profiles').select('agency_id').in('agency_id', agencyIds);
      const agentCounts: Record<string, number> = {};
      (agentProfiles || []).forEach((p: any) => { if (p.agency_id) agentCounts[p.agency_id] = (agentCounts[p.agency_id] || 0) + 1; });
      return (data || []).map(a => ({ ...a, agentCount: agentCounts[a.id] || 0 }));
    },
  });

  const filtered = (agencies || []).filter(a => {
    if (search && !a.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (cityFilter && a.city !== cityFilter) return false;
    return true;
  }).sort((a: any, b: any) => {
    if (sortBy === 'name_asc') return a.name.localeCompare(b.name);
    if (sortBy === 'name_desc') return b.name.localeCompare(a.name);
    if (sortBy === 'agents') return b.agentCount - a.agentCount;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  const activeFilterCount = [cityFilter].filter(Boolean).length;

  return (
    <Layout>
      <SEOHead
        title="Real Estate Agencies in Ethiopia — Verified Agents"
        description="Find verified real estate agencies and agents in Ethiopia. Connect with trusted professionals for buying, selling and renting property."
        canonical="/agencies"
      />
      <div className="border-b bg-muted/30">
        <div className="container py-4">
          <nav className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <ChevronRightIcon className="h-3 w-3" />
            <span className="text-foreground font-medium">Agencies</span>
          </nav>
          <div className="flex items-center justify-between">
            <h1 className="font-display text-xl md:text-2xl font-bold">Real Estate Agencies</h1>
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">{filtered.length}</span> agenc{filtered.length !== 1 ? 'ies' : 'y'} found
            </p>
          </div>
        </div>
      </div>

      {/* Sticky Filter Bar */}
      <div className="sticky top-[96px] md:top-[104px] z-30 bg-background border-b shadow-sm">
        <div className="container">
          <div className="flex items-center gap-2 py-3 overflow-x-auto scrollbar-hide">
            <div className="relative shrink-0">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input placeholder="Search agencies..." value={search} onChange={e => setSearch(e.target.value)} className="h-9 w-52 pl-8 text-xs" />
            </div>

            <Select value={cityFilter || 'all'} onValueChange={v => setCityFilter(v === 'all' ? undefined : v)}>
              <SelectTrigger className="h-9 w-auto min-w-[130px] text-xs shrink-0 gap-1.5">
                <Home className="h-3.5 w-3.5 text-muted-foreground" /><SelectValue placeholder="All Cities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cities</SelectItem>
                {CITIES.map(c => <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>)}
              </SelectContent>
            </Select>

            <div className="h-6 w-px bg-border shrink-0 mx-1" />

            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="h-9 w-auto min-w-[130px] text-xs shrink-0"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="name_asc">Name A-Z</SelectItem>
                <SelectItem value="name_desc">Name Z-A</SelectItem>
                <SelectItem value="agents">Most Agents</SelectItem>
                <SelectItem value="newest">Newest</SelectItem>
              </SelectContent>
            </Select>

            <div className="hidden md:flex border rounded-lg overflow-hidden shrink-0 ml-auto">
              <Button variant={layout === 'grid' ? 'secondary' : 'ghost'} size="icon" className="h-9 w-9 rounded-none" onClick={() => setLayout('grid')}><LayoutGrid className="h-4 w-4" /></Button>
              <Button variant={layout === 'list' ? 'secondary' : 'ghost'} size="icon" className="h-9 w-9 rounded-none" onClick={() => setLayout('list')}><List className="h-4 w-4" /></Button>
            </div>
          </div>

          {activeFilterCount > 0 && (
            <div className="flex items-center gap-2 pb-3 overflow-x-auto scrollbar-hide">
              <span className="text-[10px] text-muted-foreground shrink-0 uppercase tracking-wider font-medium">Active:</span>
              {cityFilter && <Badge variant="secondary" className="text-[10px] gap-1 shrink-0">{cityFilter}<X className="h-2.5 w-2.5 cursor-pointer" onClick={() => setCityFilter(undefined)} /></Badge>}
              <Button variant="ghost" size="sm" className="h-6 text-[10px] text-destructive shrink-0" onClick={() => setCityFilter(undefined)}>Clear All</Button>
            </div>
          )}
        </div>
      </div>

      {/* Cards Grid */}
      <div className="container py-6">
        {isLoading ? (
          <div className={`grid gap-6 ${layout === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}>
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-56 rounded-2xl" />)}
          </div>
        ) : filtered.length > 0 ? (
          <div className={`grid gap-6 ${layout === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}>
            {filtered.map((agency: any) => (
              <Link key={agency.id} to={`/agencies/${agency.id}`} className="group rounded-2xl border bg-card overflow-hidden hover:shadow-xl transition-all duration-300">
                <div className="h-24 bg-gradient-to-r from-primary/10 via-primary/5 to-accent/10 relative">
                  <Avatar className="absolute -bottom-8 left-5 h-16 w-16 border-4 border-background shadow-md">
                    <AvatarImage src={agency.logo_url} />
                    <AvatarFallback className="text-lg font-bold bg-primary/10 text-primary">{agency.name.slice(0, 2).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  {agency.is_verified && (
                    <Badge className="absolute top-3 right-3 bg-emerald-500/90 text-white border-0 text-[11px] gap-1"><Shield className="h-3 w-3" /> Verified</Badge>
                  )}
                </div>
                <div className="p-5 pt-10">
                  <h3 className="font-display text-lg font-bold group-hover:text-primary transition-colors">{agency.name}</h3>
                  {agency.city && (
                    <p className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1">
                      <MapPin className="h-3.5 w-3.5" /> {agency.city}
                      {agency.address && <span className="truncate">· {agency.address}</span>}
                    </p>
                  )}
                  {agency.description && (
                    <p className="text-sm text-muted-foreground mt-3 line-clamp-2">
                      {typeof agency.description === 'string' ? agency.description : (agency.description as any)?.en || JSON.stringify(agency.description)}
                    </p>
                  )}
                  <div className="flex items-center gap-4 mt-4 pt-4 border-t text-sm">
                    <span className="flex items-center gap-1.5 text-muted-foreground">
                      <Users className="h-4 w-4 text-primary" />
                      <span className="font-semibold text-foreground">{agency.agentCount}</span> Agents
                    </span>
                    {agency.subscription_tier && agency.subscription_tier !== 'free' && (
                      <Badge variant="secondary" className="text-[10px] capitalize">{agency.subscription_tier}</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
                    {agency.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {agency.phone}</span>}
                    {agency.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {agency.email}</span>}
                    {agency.website && <span className="flex items-center gap-1"><Globe className="h-3 w-3" /> Website</span>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <Building2 className="h-14 w-14 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-semibold mb-1">No agencies found</h3>
            <p className="text-muted-foreground text-sm">Try adjusting your search</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
